import { useRouter } from 'next/router';
import * as React from 'react';
import { PaginationSection } from 'components/src/pagination';
import Label from 'components/src/label';
import Link from 'next/link';
import Image from 'next/image';
import { formatSearchLink } from '@/utils/utils';
import SearchBox from './SearchBox';
import { SearchItemsTypesQuery, SearchItemsQueryVariables } from './Types';
import { useQuery } from '@apollo/client';
import { SearchItems } from '@/clients/queries/search/SearchItems.graphql';
import { Maybe, SearchSuggestionFieldsFragment } from 'types/generated';
import SearchInfoText from './SearchInfoText';
import SearchSuggestions from './SearchSuggestions';
import { GET_PRODUCT_CARDS_EXTRAS } from '@/clients/queries/webstore/Webstore';
import { apolloClient as contentfulApolloClient } from '@/utils/contentful';

type ProductCardsExtrasResult = {
  productCardStoreCollection?: {
    items?: Array<{
      id?: string | null;
      itemsCollection?: {
        items?: Array<{
          imagesCollection?: {
            items?: Array<{ image?: { url?: string | null } | null } | null> | null;
          } | null;
        } | null> | null;
      } | null;
    } | null> | null;
  } | null;
};

const PAGE_SIZE = 6;
const FETCH_SIZE = 60;

type Props = {
  suggestions: Array<Maybe<SearchSuggestionFieldsFragment>>;
};

export interface HighlightedField {
  source?: string;
  text: string | string[];
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const removeLeadingTitleHtml = (html: string, title: string) => {
  if (!html || !title) return html;

  const words = title.trim().split(/\s+/).map(escapeRegExp);

  const pattern = new RegExp('^\\s*' + words.map((w) => `(?:<em>\\s*)?${w}(?:\\s*<\\/em>)?`).join('\\s+'), 'i');

  return html.replace(pattern, '').trim();
};

const SearchResult = ({
  title,
  uri,
  type,
  image,
  highlighted,
}: {
  title: string;
  uri: string;
  type: string;
  image: string;
  highlighted: HighlightedField[];
}) => {
  const isProduct = type === 'product';
  return (
    <div className="mt-40">
      <div className="flex items-center gap-8">
        {isProduct && <Label label="Vefverslun" type="pill" colorVariant="disabled" />}
        <Link
          href={formatSearchLink(uri, type)}
          className="block w-fit text-md text-red-100 hover:underline hover:decoration-red-100"
        >
          {title}
        </Link>
      </div>
      <div className="flex flex-col items-center justify-start md:flex-row md:items-start md:gap-16">
        {image && (
          <div className="w-150 h-150 relative mt-16">
            <Image src={image} width={150} height={150} alt={title} />
          </div>
        )}
        {highlighted.map((field, fieldIndex) => (
          <p key={fieldIndex} className="mt-8 text-sm text-black-100">
            {Array.isArray(field.text) ? (
              field.text.map((html, i) => (
                <div key={i}>
                  <span
                    className="search-highlight"
                    dangerouslySetInnerHTML={{
                      __html:
                        fieldIndex === 0 && i === 0
                          ? removeLeadingTitleHtml(html, title) // ✅ only first field + first string
                          : html,
                    }}
                  />
                </div>
              ))
            ) : (
              <div key={fieldIndex}>
                <span
                  className="search-highlight"
                  dangerouslySetInnerHTML={{
                    __html:
                      fieldIndex === 0
                        ? removeLeadingTitleHtml(field.text, title) // ✅ only first field
                        : field.text,
                  }}
                />
              </div>
            )}
          </p>
        ))}
      </div>
    </div>
  );
};

const SearchPage = ({ suggestions }: Props) => {
  const router = useRouter();
  const [searchValue, setSearchValue] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const minSearchStringLength = 3;
  const startSearch = searchValue ? searchValue.length >= minSearchStringLength : false;

  React.useEffect(() => {
    const query = router.query;

    if (query && query.query !== undefined) {
      const queryValue = Array.isArray(query.query) ? query.query[0] : query.query;
      setSearchValue(decodeURI(queryValue));
    }
  }, [router]);

  React.useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const { data, loading } = useQuery<SearchItemsTypesQuery, SearchItemsQueryVariables>(SearchItems, {
    variables: {
      searchString: searchValue || '',
      from: 0,
      size: FETCH_SIZE,
    },
    skip: !startSearch,
  });

  const extractProductId = (item: { id: string; uri: string }) => {
    const fromUri = item.uri?.split('/').filter(Boolean).pop();
    return decodeURIComponent(fromUri || item.id || '');
  };

  const productIdsInResults = React.useMemo(() => {
    return (data?.searchItems ?? [])
      .filter((it) => it.type === 'product')
      .map((it) => extractProductId(it))
      .filter(Boolean);
  }, [data]);

  const { data: extrasData } = useQuery<ProductCardsExtrasResult>(GET_PRODUCT_CARDS_EXTRAS, {
    client: contentfulApolloClient,
    variables: { ids: productIdsInResults, limitItems: 10, limitImages: 1 },
    skip: productIdsInResults.length === 0,
  });

  const publishedProductIds = React.useMemo(() => {
    const set = new Set<string>();
    (extrasData?.productCardStoreCollection?.items ?? []).forEach((c) => {
      if (!c?.id) return;
      const hasImage = c.itemsCollection?.items?.some((it) =>
        (it?.imagesCollection?.items ?? []).some((img) => img?.image?.url)
      );
      if (hasImage) set.add(c.id);
    });
    return set;
  }, [extrasData]);

  const filteredItems = (data?.searchItems ?? []).filter((item) => {
    if (item.type !== 'product') return true;
    return publishedProductIds.has(extractProductId(item));
  });

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const noDataFound = !loading && filteredItems.length === 0;

  return (
    <div className="w-full">
      <div className="bg-white-15 py-64">
        <div className="mx-auto w-full max-w-screen-xl px-16 md:px-64">
          <SearchBox searchValue={searchValue} setSearchValue={setSearchValue} />
        </div>
      </div>
      <div className="bg-white-15 pb-128 pt-32">
        <div className="mx-auto w-full max-w-screen-xl px-16 md:px-64">
          <div className="min-h-[500px]">
            <SearchInfoText
              searchValue={searchValue}
              loading={loading}
              noDataFound={startSearch && noDataFound}
              startSearch={startSearch}
            />
            {startSearch ? (
              visibleItems.map((item) => (
                <SearchResult
                  key={item.id}
                  title={item.heading}
                  uri={item.uri}
                  type={item.type}
                  image={item.image}
                  highlighted={item.highlight}
                />
              ))
            ) : (
              <SearchSuggestions suggestions={suggestions} onItemClicked={(item) => setSearchValue(item)} />
            )}
            {totalPages > 1 && visibleItems.length > 0 && (
              <>
                <div className="my-32 w-full border-t-1 border-black-25" />
                <PaginationSection
                  pages={totalPages}
                  currentPage={currentPage}
                  alignment="center"
                  onClick={(page) => {
                    setPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default SearchPage;
